'use server';

import { pianoAuthWithGoogleAction, type PianoAuthResult } from '@/app/_actions/GoogleOneTap/pianoAuthActions';

interface GoogleTokenPayload {
  iss: string;
  aud: string;
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
  locale?: string;
  hd?: string; // Hosted domain for Google Workspace accounts
  exp: number;
  iat: number;
}

interface VerificationResult {
  success: boolean;
  payload?: GoogleTokenPayload;
  error?: string;
}

interface SessionResult {
  success: boolean;
  pianoUid?: string;
  pianoToken?: string;
  isNewUser?: boolean;
  message?: string;
  error?: string;
}

/**
 * Verify Google ID token using Google's tokeninfo endpoint
 * This is a simpler approach suitable for most use cases.
 * For production with high volume, consider using Google API client libraries.
 */
export async function verifyGoogleIdToken(idToken: string): Promise<VerificationResult> {
  try {
    // Verify the token using Google's tokeninfo endpoint
    const response = await fetch(`https://oauth2.googleapis.com/tokeninfo?id_token=${idToken}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        success: false,
        error: 'Failed to verify token with Google',
      };
    }

    const payload = (await response.json()) as GoogleTokenPayload;

    // Verify the audience (client ID)
    const expectedClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    if (payload.aud !== expectedClientId) {
      return {
        success: false,
        error: 'Token audience does not match expected client ID',
      };
    }

    // Verify the issuer
    if (payload.iss !== 'accounts.google.com' && payload.iss !== 'https://accounts.google.com') {
      return {
        success: false,
        error: 'Invalid token issuer',
      };
    }

    // Verify the token hasn't expired
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) {
      return {
        success: false,
        error: 'Token has expired',
      };
    }

    // Token is valid
    return {
      success: true,
      payload,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Create user session data from verified Google token
 * This calls our Piano authentication server action
 */
export async function createGoogleUserSession(payload: GoogleTokenPayload): Promise<SessionResult> {
  try {
    // Call our Piano authentication server action
    const result: PianoAuthResult = await pianoAuthWithGoogleAction({
      email: payload.email,
      givenName: payload.given_name,
      familyName: payload.family_name,
    });
    if (!result.success) {
      return {
        success: false,
        error: result.message || 'Piano authentication failed',
      };
    }

    return {
      success: true,
      pianoUid: result.pianoUid,
      pianoToken: result.pianoToken,
      isNewUser: result.isNewUser,
      message: result.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Piano authentication failed',
    };
  }
}

/**
 * Alternative verification using direct JWT decoding (for development only)
 * This method doesn't verify the signature, so it should only be used for development
 */
export function decodeGoogleIdToken(idToken: string): GoogleTokenPayload | null {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as GoogleTokenPayload;
  } catch {
    return null;
  }
}
